import { SURFACE_HEADER } from "@hakili/contracts";

import { OPERATIONS, type ApiMethod, type ApiPath } from "./generated/paths.js";

/**
 * `P0-UIK-015`: the request layer, and the reason an app never writes a URL.
 *
 * 4.6.2: *"The six apps import only `@hakili/api-client`. No app constructs a
 * URL string."* 4.5.11 gives this file its other two jobs: it *"injects the
 * access token, refreshes it on `401` once, and maps the error envelope into a
 * typed `ApiError`"*.
 *
 * **Danger sentence.** A screen that builds `/api/v1/users/${id}` compiles
 * whatever it is given, so `undefined` becomes a path segment and the caller
 * gets a `404` about a user nobody asked for. Worse, the path is then invisible
 * to `P0-PLT-033`'s surface check: that guard compares MOUNTED routes against
 * DECLARED ones, and a hand-built string is neither , it is a call to a route
 * that may have been renamed three commits ago, and nothing anywhere notices
 * until a screen goes blank in Bamako.
 *
 * So a call names an OPERATION from the generated union. A path that is not in
 * the document is a compile error, which is the whole return on having a
 * document that finally describes the application.
 *
 * **The refresh is here and nowhere else.** Exactly one refresh may be in
 * flight: two independent refreshers race, and the loser writes back a token
 * the winner already rotated, signing the user out at random on a slow
 * connection , which is 4.6.1's stated network. The single flight is a promise
 * held in a field, not a boolean, because a boolean tells the second caller to
 * skip and gives it nothing to wait for.
 */

export interface ApiErrorBody {
  readonly error?: { readonly code?: string; readonly message?: string; readonly details?: unknown };
}

/** 4.5.5's envelope, as the shell reads it: a status and the server's own sentence. */
export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly details: unknown;

  public constructor(status: number, body: unknown) {
    const envelope = (body ?? {}) as ApiErrorBody;
    // The server's French sentence, carried through untouched. 4.5.5 writes it
    // beside the guard that produced it, and a client that replaced it with a
    // generic apology would show a vague message where a precise one was sent.
    super(envelope.error?.message ?? "");
    this.name = "ApiError";
    this.status = status;
    this.code = envelope.error?.code ?? "unknown";
    this.details = envelope.error?.details;
  }
}

export interface RequestOptions {
  /** Path parameters, by name. `{id}` in the declared path is filled from here. */
  readonly params?: Readonly<Record<string, string>>;
  /**
   * Query parameters, by name. **Values are encoded here and never
   * interpolated by a caller**: a screen that built its own `?archive=true`
   * would be one un-escaped value away from a malformed URL, and the first
   * value to carry an `&` would silently become two parameters.
   *
   * Added in round 189 for `P1-LND-034`'s archive filter, which is the first
   * declared operation this client calls with one.
   */
  readonly query?: Readonly<Record<string, string>>;
  readonly body?: unknown;
  /** Sent as `Idempotency-Key` where the operation wants one (4.5.11). */
  readonly idempotencyKey?: string;
  readonly signal?: AbortSignal;
}

export interface ApiClientOptions {
  /**
   * Where the API lives. No default: a client-side default is how a build ends
   * up talking to somebody else's environment, and the one time it matters is
   * the one time nobody checks.
   */
  readonly origin: string;
  /** Called when the refresh itself fails, meaning the session is genuinely over. */
  readonly onSessionEnded?: () => void;
  readonly fetch?: typeof globalThis.fetch;
  /**
   * `P0-IDN-063`, `00-SPINE` 6.6: **which surface this application IS.**
   *
   * The api names the surface of a request from its `Origin` and cross-checks
   * it against this declaration, and issues one refresh cookie per surface, so
   * a session opened on dealer-web cannot be revived by the console.
   *
   * The `Origin` is the browser's own word and the stronger of the two; this is
   * the application's, and its value is the DISAGREEMENT: a build served from
   * the wrong host says so here rather than quietly inheriting that host's
   * sessions.
   */
  readonly surface?: "console" | "dealer" | "bank" | "client" | "expert";
}

// `§5.35`: the name comes from the CONTRACT, not from a literal here. It was a
// literal until the deploy of `#292` made staging unusable , the api's CORS
// carried its own hand-written list and nothing joined the two.

const declared = (path: ApiPath, method: ApiMethod): boolean =>
  OPERATIONS.some((operation) => operation.path === path && operation.method === method);

/** `/api/v1/users/{id}` plus `{ id: "42" }`. Every segment is encoded. */
export function fillPath(path: string, params: Readonly<Record<string, string>> = {}): string {
  return path.replaceAll(/\{([A-Za-z]+)\}/gu, (_, name: string) => {
    const value = params[name];
    if (value === undefined || value === "") {
      // An empty segment collapses the path and sends the request somewhere
      // else entirely , `/users//deactivate` is not `/users/{id}/deactivate`.
      // Refused here, where the name of the missing parameter is still known.
      throw new Error(`the path parameter "${name}" of ${path} is missing`);
    }
    return encodeURIComponent(value);
  });
}

/**
 * The answers whose staleness would cost a session, and only those.
 *
 * `/me` is who the caller IS. A cached one seats a person on an identity the
 * server may have revoked, renamed or re-scoped since, and every screen behind
 * it then renders a lie the browser is confident about. The dossier reads
 * beside it are ordinary data: a stale file is a refresh away and costs nobody
 * a session.
 */
const identityPath = (path: string): boolean => path === "/api/v1/me";

/**
 * The `401`s that mean *your secret is wrong*, not *your session is spent*.
 *
 * 4.5.5 gives both meanings the same status, which is correct at the HTTP layer
 * and ambiguous at this one. Refreshing on a credential refusal spends a cookie
 * the caller does not have and ends a session that was never open , which on 29
 * August 2026 sent a buyer to the landing screen for the crime of mistyping four
 * digits.
 *
 * Read from the envelope's `code`, the field 4.5.5 exists to make readable.
 */
const CREDENTIAL_REFUSALS: ReadonlySet<string> = new Set(["pin_invalid", "otp_invalid"]);

const credentialRefusal = (body: unknown): boolean => {
  const code = (body as { error?: { code?: unknown } } | null)?.error?.code;
  return typeof code === "string" && CREDENTIAL_REFUSALS.has(code);
};

/**
 * `S5-1 (b)`: **the anonymous credential door.** `POST /auth/client/pin/login` is called by a caller who holds no session, so a `401` from it
 * can only be a refusal of what was presented, never a spent session. Its first gate, the handset, answers `401 unauthenticated` with
 * « Cet appareil n'est pas reconnu. » (the code is shared with an expired session on purpose: the sentence, not the code, tells the
 * handset apart, `client-devices.ts`). Read by the STATUS and the code alone, it triggered a refresh the caller had no cookie for, the
 * refresh failed, `onSessionEnded` fired and the buyer saw nothing. The door decides, as the code decides for `pin_invalid` above.
 */
const CREDENTIAL_DOORS: ReadonlySet<string> = new Set([
  "/api/v1/auth/client/pin/login",
  // The same class at its second anonymous door (device cap M0, 16 October 2026): `register` answers an expired or unknown
  // enrolment `401 unauthenticated` « Votre vérification a expiré. Recommencez. », to a caller who holds no session.
  "/api/v1/auth/client/register",
]);

export class ApiClient {
  readonly #options: ApiClientOptions;
  readonly #fetch: typeof globalThis.fetch;
  #accessToken: string | null = null;
  /** The single in-flight refresh. A promise, not a flag , see the class comment. */
  #refreshing: Promise<boolean> | null = null;

  public constructor(options: ApiClientOptions) {
    this.#options = options;
    this.#fetch = options.fetch ?? globalThis.fetch.bind(globalThis);
  }

  public get accessToken(): string | null {
    return this.#accessToken;
  }

  public setAccessToken(token: string | null): void {
    this.#accessToken = token;
  }

  /**
   * One call to one DECLARED operation.
   *
   * `path` and `method` are the generated union, so a route the document does
   * not describe cannot be requested , and after `P0-PLT-033` the document
   * describes the application rather than one module of it.
   */
  public async request<T>(path: ApiPath, method: ApiMethod, options: RequestOptions = {}): Promise<T> {
    const response = await this.#call(path, method, options, "application/json");
    if (!response.ok) throw new ApiError(response.status, await this.#json(response));
    return (await this.#json(response)) as T;
  }

  /**
   * `P1-ITL-043`, round 199g: **one declared operation that answers BYTES.**
   *
   * The bank panel shows the scan an analyst's file carries, and that page sits
   * behind the same authorisation as everything else , which is precisely why it
   * cannot be an `<img src>`. **Round 166 §1.2: the access token lives in memory
   * only, never in a URL, never in a readable cookie.** An `img` element sends
   * cookies and nothing else, so a src pointing at this route would either be
   * refused or would force the token somewhere §1.2 forbids. The screen fetches
   * it here, with the header every other call already uses, and renders the
   * answer from an object URL.
   *
   * Everything else , the declared-operation check, the single `401` refresh,
   * the credential-refusal rule , is the same code path as `request`, because a
   * second implementation of the auth dance is a second thing to get wrong.
   */
  public async blob(path: ApiPath, method: ApiMethod, options: RequestOptions = {}): Promise<Blob> {
    const response = await this.#call(path, method, options, "image/png");
    if (!response.ok) throw new ApiError(response.status, await this.#json(response));
    return response.blob();
  }

  async #call(path: ApiPath, method: ApiMethod, options: RequestOptions, accept: string): Promise<Response> {
    if (!declared(path, method)) {
      // Reachable only from untyped data. The union already refuses this at
      // compile time; this refuses it where a `as ApiPath` cast got through.
      throw new Error(`${method} ${path} is not an operation this API declares`);
    }

    const send = async (): Promise<Response> => {
      const headers: Record<string, string> = { accept };
      // Sent on EVERY call, not only the auth ones: the api names a surface
      // wherever it needs one, and a header present on some requests and absent
      // on others is a rule with a hole in it.
      if (this.#options.surface !== undefined) headers[SURFACE_HEADER] = this.#options.surface;
      if (this.#accessToken !== null) headers["authorization"] = `Bearer ${this.#accessToken}`;
      if (options.body !== undefined) headers["content-type"] = "application/json";
      if (options.idempotencyKey !== undefined) headers["idempotency-key"] = options.idempotencyKey;

      // Built as a value first: under `exactOptionalPropertyTypes` a spread of
      // a maybe-absent key does not narrow to "the key is absent", so the
      // optional members are assigned only when they exist.
      const init: RequestInit = {
        method: method.toUpperCase(),
        headers,
        // The refresh token is an HttpOnly cookie (6.1.3), so every call has to
        // carry credentials or the refresh has nothing to present.
        credentials: "include",
        // `P1-PLT-042`. **Identity is never served from a cache.** The API sets
        // no `Cache-Control` and Express stamps a weak `ETag` by default, which
        // is the recipe for heuristic freshness plus revalidation: on 30 August
        // 2026 the founder's `/me` showed a `304` on the wire while the code was
        // handed the stored body. It was not that sitting's defect , the shapes
        // had never agreed, cache or no cache , but a STALE identity is a
        // security-shaped bug and not a performance question, so this door is
        // shut rather than watched.
        //
        // **`no-store` and never explicit `304` handling.** With the default
        // cache mode the browser revalidates and hands JavaScript the cached
        // `200`; a literal `304` is a status `fetch` does not deliver here, and
        // code written to catch it would be code written against a misreading.
        // Chosen per request rather than for the whole client: identity is the
        // one answer whose staleness costs a session.
        ...(identityPath(path) ? { cache: "no-store" as const } : {}),
      };
      if (options.body !== undefined) init.body = JSON.stringify(options.body);
      if (options.signal !== undefined) init.signal = options.signal;

      const search = options.query === undefined ? "" : `?${new URLSearchParams(options.query).toString()}`;
      return this.#fetch(`${this.#options.origin}${fillPath(path, options.params)}${search}`, init);
    };

    let response = await send();

    // 4.5.11: refreshed on 401 ONCE. Never on the refresh route itself, which
    // would recurse, and never on a 403 , that says the session is fine and
    // this action is not yours.
    //
    // **And never on a CREDENTIAL refusal, which round 185 found the hard way.**
    // `pin_invalid` and `otp_invalid` are `401` by 4.5.5's own table, and this
    // branch read the STATUS alone: a buyer typing a wrong PIN at `Connexion`
    // triggered a refresh he had no cookie for, the refresh failed,
    // `onSessionEnded` fired, and he was thrown to the landing screen instead of
    // being told his code was wrong. **WF-CLI-02's whole lockout choreography ,
    // "Code incorrect. Il vous reste N essais.", then the lock , was unreachable
    // in the deployed product**, because the first wrong attempt ended the
    // session.
    //
    // The two meanings share a status and are opposite facts. `unauthenticated`
    // says *the session you had is spent*, which a refresh can repair.
    // `pin_invalid` and `otp_invalid` say *the secret you just typed is wrong*,
    // and there is no session to repair , the caller was anonymous by design.
    // So the CODE decides, not the number.
    if (response.status === 401 && path !== "/api/v1/auth/refresh") {
      // Read once: a body is a stream, and the retry path below re-sends rather
      // than re-reading.
      const refusal = await this.#json(response);
      if (credentialRefusal(refusal) || CREDENTIAL_DOORS.has(path)) throw new ApiError(401, refusal);
      if (await this.#refreshOnce()) response = await send();
      else throw new ApiError(401, refusal);
    }

    return response;
  }

  /** Exchanges the refresh cookie for a new access token. At most one in flight. */
  async #refreshOnce(): Promise<boolean> {
    this.#refreshing ??= (async (): Promise<boolean> => {
      try {
        const response = await this.#fetch(`${this.#options.origin}/api/v1/auth/refresh`, {
          method: "POST",
          headers: {
            accept: "application/json",
            ...(this.#options.surface === undefined ? {} : { [SURFACE_HEADER]: this.#options.surface }),
          },
          credentials: "include",
        });
        if (!response.ok) {
          this.#accessToken = null;
          this.#options.onSessionEnded?.();
          return false;
        }
        const body = (await this.#json(response)) as { accessToken?: string };
        if (typeof body.accessToken !== "string") return false;
        this.#accessToken = body.accessToken;
        return true;
      } finally {
        // Cleared whatever happened, so a later 401 can try again. Left set, a
        // single failed refresh would make every future call unrecoverable.
        this.#refreshing = null;
      }
    })();
    return this.#refreshing;
  }

  async #json(response: Response): Promise<unknown> {
    const text = await response.text();
    if (text === "") return null;
    try {
      return JSON.parse(text);
    } catch {
      // A proxy's HTML error page, or a truncated body on a dropped 3G
      // connection. Returning it raw lets `ApiError` carry the status, which is
      // the part the shell acts on, instead of throwing a parse error that
      // hides it.
      return { error: { code: "unparseable", message: "" } };
    }
  }
}
